/**
 * Family Dashboard Page
 * Coláiste Lurgan Management System - Parent view of their children's info and payments
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Loader2, Users, GraduationCap, Home as HomeIcon,
  CalendarDays, ArrowRight, Wallet, Award, Bus, AlertTriangle,
} from 'lucide-react';
import { useMyChildren } from '../../hooks/useMyChildren';

// Stable colour palette for avatar / border accents
const ACCENT_COLOURS = [
  { border: 'border-l-violet-400', avatar: 'bg-violet-100 text-violet-700', badge: 'text-violet-700 border-violet-300' },
  { border: 'border-l-blue-400',   avatar: 'bg-blue-100 text-blue-700',     badge: 'text-blue-700 border-blue-300' },
  { border: 'border-l-teal-400',   avatar: 'bg-teal-100 text-teal-700',     badge: 'text-teal-700 border-teal-300' },
  { border: 'border-l-indigo-400', avatar: 'bg-indigo-100 text-indigo-700', badge: 'text-indigo-700 border-indigo-300' },
  { border: 'border-l-amber-400',  avatar: 'bg-amber-100 text-amber-700',   badge: 'text-amber-700 border-amber-300' },
  { border: 'border-l-sky-400',    avatar: 'bg-sky-100 text-sky-700',       badge: 'text-sky-700 border-sky-300' },
];

function colourForName(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return ACCENT_COLOURS[Math.abs(hash) % ACCENT_COLOURS.length];
}

function initials(firstName = '', lastName = '') {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function formatCurrency(value) {
  const num = parseFloat(value);
  if (isNaN(num)) return null;
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(num);
}

function ChildCard({ child, onNavigate, isPast = false }) {
  const { t } = useTranslation();
  const colour = colourForName(`${child.first_name}${child.last_name}`);
  const balanceDue = parseFloat(child.balance_due);
  const hasBalance = !isNaN(balanceDue) && balanceDue > 0;

  const isCancelled = child.status === 'CANCELLED' || child.has_canceled;
  const isDimmed = isCancelled || isPast;

  return (
    <Card
      className={`flex flex-col border-l-4 ${isDimmed ? 'border-l-gray-300 opacity-70' : colour.border} hover:shadow-md transition-shadow cursor-pointer`}
      onClick={() => onNavigate(child.uuid)}
    >
      <CardContent className="flex-1 p-5 space-y-4">

        {/* Avatar + Name */}
        <div className="flex items-center gap-3">
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${isDimmed ? 'bg-gray-100 text-gray-500' : colour.avatar}`}>
            {initials(child.first_name, child.last_name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-base leading-tight truncate">
                {child.first_name} {child.last_name}
              </p>
              {isPast && (
                <Badge variant="outline" className="text-xs text-muted-foreground border-gray-300 shrink-0">
                  {t('familyDashboard.pastStudent')}
                </Badge>
              )}
              {!isPast && isCancelled && (
                <Badge variant="outline" className="text-xs text-red-600 border-red-300 shrink-0">
                  {t('familyDashboard.cancelled')}
                </Badge>
              )}
            </div>
            {child.student_id && (
              <p className="text-xs text-muted-foreground mt-0.5">{child.student_id}</p>
            )}
          </div>
        </div>

        {/* Info badges */}
        <div className="flex flex-wrap gap-1.5">
          {child.course_name && (
            <Badge variant="outline" className={`text-xs ${colour.badge}`}>
              <GraduationCap className="h-3 w-3 mr-1" />
              {child.course_name}
            </Badge>
          )}
          {child.school_year_name && (
            <Badge variant="outline" className="text-xs">
              <CalendarDays className="h-3 w-3 mr-1" />
              {child.school_year_name}
            </Badge>
          )}
          {child.house_name && (
            <Badge variant="outline" className="text-xs">
              <HomeIcon className="h-3 w-3 mr-1" />
              {child.house_name}
            </Badge>
          )}
          {child.is_cinnire && (
            <Badge className="text-xs bg-amber-500 hover:bg-amber-500">
              <Award className="h-3 w-3 mr-1" />
              {t('students.badge.cinnire')}
            </Badge>
          )}
          {child.travel_required && (
            <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
              <Bus className="h-3 w-3 mr-1" />
              {t('students.badge.travelRequired')}
            </Badge>
          )}
          {child.has_friends && (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              <Users className="h-3 w-3 mr-1" />
              {t('students.badge.hasFriends')}
            </Badge>
          )}
        </div>

        {/* Payment summary */}
        {(child.total_paid !== undefined || child.balance_due !== undefined) && (
          <div className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${hasBalance ? 'bg-amber-50 border border-amber-200' : 'bg-muted/50'}`}>
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Wallet className="h-3.5 w-3.5" />
              <span>{t('familyDashboard.paid')}: {formatCurrency(child.total_paid) ?? '—'}</span>
            </div>
            {hasBalance && (
              <span className="text-amber-700 font-medium text-xs">
                {t('familyDashboard.balance')}: {formatCurrency(child.balance_due)}
              </span>
            )}
          </div>
        )}

        {/* Missing-data warnings — not shown for past/read-only students */}
        {!isPast && !child.student_email && !isCancelled && (
          <div className="flex items-center gap-2 rounded-md bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{t('myStudent.missingEmailBanner')}</span>
          </div>
        )}
        {!isPast && !child.parent_phone && !isCancelled && (
          <div className="flex items-center gap-2 rounded-md bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{t('myStudent.missingPhoneBanner')}</span>
          </div>
        )}
        {!isPast && !child.address_1 && !isCancelled && (
          <div className="flex items-center gap-2 rounded-md bg-orange-50 border border-orange-200 px-3 py-2 text-sm text-orange-700">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{t('myStudent.missingAddressBanner')}</span>
          </div>
        )}

        {/* Footer link */}
        <div className="pt-1 border-t">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between text-muted-foreground hover:text-foreground min-h-[44px]"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(child.uuid);
            }}
          >
            {(isPast || isCancelled) ? t('familyDashboard.viewDetails') : t('familyDashboard.viewAndEdit')}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

      </CardContent>
    </Card>
  );
}

export function FamilyDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: allChildren = [], isLoading, error } = useMyChildren();
  const children = allChildren.filter((child) => child.status !== 'ARCHIVED');
  const pastChildren = allChildren.filter((child) => child.status === 'ARCHIVED');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold tracking-tight border-l-4 pl-3" style={{ borderColor: '#F28482' }}>{t('familyDashboard.title')}</h1>
        <p className="text-destructive text-sm">{t('common.errorLoading')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-xl px-4 py-4 sm:px-6 sm:py-5" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900">{t('familyDashboard.title')}</h1>
        <p className="text-gray-700 mt-1">{t('familyDashboard.description')}</p>
      </div>

      {/* Empty state */}
      {children.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground">
            <Users className="h-12 w-12 mb-4 opacity-30" />
            <p className="text-sm">{t('familyDashboard.noChildren')}</p>
          </CardContent>
        </Card>
      )}

      {/* Rich summary card per child */}
      {children.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {children.map((child) => (
            <ChildCard key={child.uuid} child={child} onNavigate={(uuid) => navigate(`/my-student/${uuid}`)} />
          ))}
        </div>
      )}

      {/* Past students — separate, read-only section (docs/CLARU_INTEGRATION_PLAN.md §3) */}
      {pastChildren.length > 0 && (
        <div className="space-y-3 pt-2">
          <h2 className="text-lg font-semibold text-muted-foreground">{t('familyDashboard.pastStudents')}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {pastChildren.map((child) => (
              <ChildCard
                key={child.uuid}
                child={child}
                onNavigate={(uuid) => navigate(`/my-student/${uuid}`)}
                isPast
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default FamilyDashboard;
